import { FirebaseApp } from 'firebase/app';
import { doc, setDoc, writeBatch } from 'firebase/firestore';
import {
  getFunctions,
  httpsCallable,
  HttpsCallableResult,
} from 'firebase/functions';
import chunk from 'lodash/chunk';
import { promiseAny } from '../../utils/promiseAny';
import { getRegionConfig, globalApp } from '../providers/appsProvider';
import { userAsPromise } from '../providers/userProvider';
import { ICoach } from '../providers/usersProvider';
import { IPermission } from '../types';

export const updatePermission = async (
  userId: string,
  update: IPermission
): Promise<void> => {
  const requestingUser = await userAsPromise();
  await setDoc(
    doc(
      requestingUser.services.firestore,
      `userProfiles/${userId}/teams/${update.id}`
    ),
    update,
    {
      merge: true,
    }
  );
};

export const deletePermission = async (
  userId: string,
  teamId: string
): Promise<HttpsCallableResult> => {
  const requestingUser = await userAsPromise();
  const fn = httpsCallable(
    requestingUser.services.functions,
    'removeUserFromTeam'
  );
  return fn({ userId, teamId });
};

export const updatePermissions = async (
  userId: string,
  originalPermissions: IPermission[],
  permissions: IPermission[]
): Promise<void> => {
  const requestingUser = await userAsPromise();
  const originalIds = originalPermissions.map(
    (perm: IPermission): string => perm.id
  );
  const currentIds = permissions.map((perm: IPermission): string => perm.id);

  const deleteBatch = writeBatch(requestingUser.services.firestore);

  /// remove deleted permissions
  for (const id of originalIds) {
    if (!currentIds.includes(id)) {
      deleteBatch.delete(
        doc(
          requestingUser.services.firestore,
          `userProfiles/${userId}/teams/${id}`
        )
      );
    }
  }
  /// limit to 6 operations per batch due to firestore rules evaluation limit
  const permissionOps = chunk(permissions, 6);

  const permissionsCommit = permissionOps.map(
    (permissionOp: IPermission[]): Promise<void> => {
      const batch = writeBatch(requestingUser.services.firestore);
      for (const perm of permissionOp) {
        const createdPerm = {
          id: perm.id,
          write: perm.write,
          read: perm.read,
          orgId: perm.orgId,
        };
        batch.set(
          doc(
            requestingUser.services.firestore,
            `userProfiles/${userId}/teams/${perm.id}`
          ),
          createdPerm,
          { merge: true }
        );
      }
      return batch.commit();
    }
  );

  await Promise.all([permissionsCommit, deleteBatch.commit()]);

  const refresh = httpsCallable(
    requestingUser.services.functions,
    'refreshPermissions'
  );
  await refresh({ userId });
  return;
};

export const addNewUser = async (
  name: string,
  email: string,
  teamId: string,
  userType: string
): Promise<HttpsCallableResult> => {
  const requestingUser = await userAsPromise();
  const fn = httpsCallable(requestingUser.services.functions, 'addNewTeamUser');
  return fn({
    email,
    name,
    teamId,
    userType,
  });
};

export const disableUser = async (
  user: ICoach
): Promise<HttpsCallableResult> => {
  const requestingUser = await userAsPromise();
  const fn = httpsCallable(requestingUser.services.functions, 'disableUser');
  return fn({
    userId: user.id,
  });
};

/**
 * @description updates a user's email in their `/userProfile` document
 * @param email {string} the new email
 */
export const updateProfileEmail = async (
  email: string
): Promise<HttpsCallableResult> => {
  const requestingUser = await userAsPromise();
  const fn = httpsCallable(
    requestingUser.services.functions,
    'updateProfileEmail'
  );
  return fn({ email });
};

interface ITrySendResetParams {
  app: FirebaseApp;
  email: string;
}

/**
 * @description sends a password reset email using a specific Firebase app instance
 * @param email
 * @param app - The Firebase app instance to use
 * @param tenantId - Optional tenant ID from identity service lookup
 */
export const sendPasswordReset = async (
  email: string,
  app: FirebaseApp,
  tenantId?: string | null
): Promise<void> => {
  const region = getRegionConfig(app);
  const functions = getFunctions(app, region.domain);

  // Call sendEmailPasswordReset with tenantId
  const reset = httpsCallable(functions, 'sendEmailPasswordReset');
  await reset({
    email: email.trim().toLowerCase(),
    tenantId: tenantId || null,
  });
};

const trySendReset = async (args: ITrySendResetParams): Promise<void> => {
  const { email, app } = args;
  const region = getRegionConfig(app);
  const functions = getFunctions(app, region.domain);
  const reset = httpsCallable(functions, 'sendEmailPasswordReset');
  await reset({ email: email.trim().toLowerCase() });
};

interface INewPasswordArgs {
  email: string;
  password: string;
  code: string;
}

/**
 * @description sets a new password using a specific Firebase app instance
 * @param email
 * @param password
 * @param code
 * @param app - The Firebase app instance to use for the password reset
 * @param tenantId - Optional tenant ID to include in the request
 */
export const setNewPassword = async (
  email: string,
  password: string,
  code: string,
  app: FirebaseApp,
  tenantId?: string | null
): Promise<void> => {
  const region = getRegionConfig(app);
  const functions = getFunctions(app, region.domain);
  const updatePassword = httpsCallable(functions, 'resetPassword');
  await updatePassword({
    email: email.trim().toLowerCase(),
    password,
    code,
    tenantId: tenantId || null,
  });
};

interface ITryCheckResetCodeParams {
  code: string;
  email: string;
  app: FirebaseApp;
}

/**
 * @description tries to validate the reset code
 * @param args {ITryCheckResetCodeParams}
 */
export const tryCheckResetCode = async (
  args: ITryCheckResetCodeParams
): Promise<void> => {
  const { email, code, app } = args;
  const region = getRegionConfig(app);
  const functions = getFunctions(app, region.domain);
  const check = httpsCallable(functions, 'checkResetCode');
  await check({ email: email.trim().toLowerCase(), code });
};

/**
 * @description checks to see if the reset code is valid using a specific Firebase app instance
 * @param email
 * @param code
 * @param app - The Firebase app instance to use for validation
 */
export const checkPasswordResetCode = async (
  email: string,
  code: string,
  app: FirebaseApp
): Promise<void> => {
  await tryCheckResetCode({ email, code, app });
};

interface ITrySetParams {
  app: FirebaseApp;
  email: string;
  password: string;
  code: string;
}

const trySetNewPassword = async (args: ITrySetParams): Promise<void> => {
  const { app, email, password, code } = args;
  const region = getRegionConfig(app);
  const functions = getFunctions(app, region.domain);
  const updatePassword = httpsCallable(functions, 'resetPassword');
  await updatePassword({
    email: email.trim().toLowerCase(),
    password,
    code,
  });
};
